import { Component } from '@theme/component';

/**
 * One-click size picker on product cards (snippets/quick-size.liquid).
 *
 * The add itself is delegated to the nested <product-form-component>: this component only
 * writes the chosen variant id into the form's hidden `id` input and submits the form, so
 * cart requests, CartLinesUpdateEvent, the cart bubble and drawer auto-open all stay in
 * Horizon's product-form.js. On touch screens a + toggle opens and closes the panel; on
 * pointer devices the panel is revealed by CSS on hover/focus.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [toggle] - The touch toggle (card context only).
 * @property {HTMLElement} panel - The panel holding the size buttons.
 *
 * @extends Component<Refs>
 */
export class QuickSizeComponent extends Component {
  requiredRefs = ['panel'];

  /** @type {AbortController | null} */
  #outsideListeners = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#outsideListeners?.abort();
    this.#outsideListeners = null;
  }

  /**
   * Opens or closes the panel (touch toggle).
   */
  toggle() {
    if (this.hasAttribute('data-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.setAttribute('data-open', '');
    this.refs.toggle?.setAttribute('aria-expanded', 'true');

    this.#outsideListeners?.abort();
    this.#outsideListeners = new AbortController();
    const { signal } = this.#outsideListeners;

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (event.target instanceof Node && !this.contains(event.target)) this.close();
      },
      { signal }
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        this.close();
        this.refs.toggle?.focus();
      },
      { signal }
    );

    this.refs.panel.querySelector('button:not(:disabled)')?.focus();
  }

  close() {
    this.removeAttribute('data-open');
    this.refs.toggle?.setAttribute('aria-expanded', 'false');
    this.#outsideListeners?.abort();
    this.#outsideListeners = null;
  }

  /**
   * Adds the clicked size to the cart through the nested product form.
   * @param {MouseEvent} event - Click on a size button carrying data-variant-id.
   */
  add(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const { variantId } = button.dataset;
    const form = this.refs.panel.querySelector('product-form-component form');
    const idInput = form?.querySelector('input[name="id"]');
    if (!variantId || !(form instanceof HTMLFormElement) || !(idInput instanceof HTMLInputElement)) return;

    // Stop the click reaching the card link underneath.
    event.preventDefault();
    event.stopPropagation();

    idInput.value = variantId;
    button.setAttribute('aria-busy', 'true');
    setTimeout(() => button.removeAttribute('aria-busy'), 1200);

    form.requestSubmit();
    this.close();
  }
}

if (!customElements.get('quick-size-component')) {
  customElements.define('quick-size-component', QuickSizeComponent);
}
